import {Component, Input} from '@angular/core';
import {Route} from '../../enum/route';
import {Router} from '@angular/router';
import {ProposalTile} from '../../model/proposal-tile';
import {getCurrencySymbol} from '@angular/common';
import {DetailsPageTab} from '../../enum/details-page-tabs';
import {PermissionService} from '../../services/permission/permission.service';
import {UserService} from '../../services/user/user.service';
import {Store} from '@ngrx/store';
import {ProposalUser} from '../../model/proposal/proposalUser';
import {selectUser, selectUserId} from '../../store/user/selectors/user.selectors';
import LineOfBusinessColor from '../../../assets/config/line-of-business-color.json';
import {ProposalStatus} from '../../enum/proposal-status';
import ProposalStatusColors from '../../../assets/config/proposal-status-colors.json';
import {LineOfBusinessService} from '../../services/proposal-service/line-of-business.service';
import {OfferService} from '../../services/offer/offer.service';
import {map, take} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {Offer} from '../../model/offer/offer';
import {OfferStatus} from '../../enum/offer-status';
import {selectUserRoles} from '../../store/auth/selectors/auth.selectors';
import {Roles} from '../../enum/roles';
import {User} from '../../model/user';
import {ProposalService} from '../../services/proposal-service/proposal.service';

@Component({
    selector: 'app-proposal-tile',
    templateUrl: './proposal-tile.component.html',
    styleUrls: ['./proposal-tile.component.scss'],
    standalone: false
})
export class ProposalTileComponent {
  private _proposalTile: ProposalTile;

  hasOffers: boolean;
  hasOffersNeedingAction: boolean = false;
  offerActionReason: 'revision' | 'pending' | null = null;

  roles$ = this.userStore.select(selectUserRoles);
  user$ = this.userStore.select(selectUser);
  user: User;

  @Input() set proposalTile(proposalTile) {
    this.user$.pipe(take(1)).subscribe(user => this.user = user);

    this._proposalTile = proposalTile;
    this.lineOfBusinessIconName = this.getLineOfBusinessIconName();
    this.percentToDeadline = this.getPercentToDeadline();

    this.statusToShow = this.checkStatus();

    const updatedLineOfBusiness = { ...this._proposalTile.lineOfBusiness };
    if (!updatedLineOfBusiness.name) {
      this.lobService.getLineOfBusiness(proposalTile.lineOfBusiness.number)
          .subscribe(lob => updatedLineOfBusiness.name = lob.name);
    }
    this._proposalTile = { ...this._proposalTile, lineOfBusiness: updatedLineOfBusiness };

    if (proposalTile && proposalTile.id) {
      this.userStore.select(selectUserId).pipe(
        switchMap(userId =>
          this.offerService.getOffersByAd(proposalTile.id).pipe(
            map((offers: Offer[]) => {
              const userOffers = offers.filter(
                offer => offer.creator?.id === userId || this.user.representedUsers.includes(offer.creator?.id)
              );
              const offerActionReason: 'revision' | 'pending' | null =
                userOffers.some(offer => offer.status === OfferStatus.REVISION) ? 'revision' :
                userOffers.some(offer => offer.status === OfferStatus.PENDING) ? 'pending' :
                null;
              return {
                hasOffers: userOffers.length > 0,
                hasOffersNeedingAction: offerActionReason !== null,
                offerActionReason
              };
            })
          )
        )
      ).subscribe(result => {
        this.hasOffers = result.hasOffers;
        this.hasOffersNeedingAction = result.hasOffersNeedingAction;
        this.offerActionReason = result.offerActionReason;
      });
    } else {
      this.hasOffers = false;
      this.hasOffersNeedingAction = false;
      this.offerActionReason = null;
    }
  }

  get proposalTile() {
    return this._proposalTile;
  }

  @Input() showButtons = true;
  @Input() selected = false;
  @Input() transparent = false;
  @Input() detailsTile = false;
  @Input() hideCreateOffer = false;
  @Input() isPreview = false;

  ProposalStatus = ProposalStatus;
  ProposalStatusColors = ProposalStatusColors;
  statusToShow: ProposalStatus;

  userId$ = this.userStore.select(selectUserId);
  lineOfBusinessIconName: string = null;
  LineOfBusinessColor = LineOfBusinessColor;
  percentToDeadline:number = null;

  constructor(readonly router: Router,
              public readonly permissionService: PermissionService,
              public userService: UserService,
              public userStore: Store<ProposalUser>,
              public readonly offerService: OfferService,
              private readonly lobService: LineOfBusinessService,
              private readonly proposalService: ProposalService) {

  }

  navigateToCreateOffer(): void {
    this.router.navigate(['/' + Route.CREATE_OFFER, this.proposalTile.id])?.then();
  }

  navigateToDetails(): void {
    this.router.navigate(['/' + Route.PROPOSAL_DETAILS, this.proposalTile.id],
      {queryParams: {tab: DetailsPageTab.DETAILS}})?.then();
  }

  coverageColor() {
    if (this.proposalTile.coverage < 1 / 3) {
      return '#AB1400';
    } else if (this.proposalTile.coverage < 2 / 3) {
      return '#946300';
    } else {
      return '#4D9407';
    }
  }

  checkStatus(): ProposalStatus {
    if (this.getPercentToDeadline() <= 0) {
      // If current date is past the deadline, return EXPIRED status
      return ProposalStatus.EXPIRED;
    } else {
      // Otherwise, return the original status
      return this._proposalTile.status;
    }
  }

  getDurationDeadline() {
    const creationDate = new Date(this.proposalTile.creationDate);
    const deadline = new Date(this.proposalTile.deadline);
    deadline.setHours(deadline.getHours() + 24);
    return deadline.valueOf() - creationDate.valueOf();
  }

  getTimeToDeadline() {
    let durationNow = new Date().getTime() - new Date(this.proposalTile.creationDate).valueOf();
    return this.getDurationDeadline() - durationNow;
  }

  getPercentToDeadline() {
    return (this.getTimeToDeadline() / this.getDurationDeadline())*100;
  }

  deadlineColor() {
    if (this.getPercentToDeadline() <= 0) {
      return 'rgba(158, 0, 56)';
    } else if (this.getPercentToDeadline() < 10) {
      return 'rgb(158, 0, 56)';
    } else if (this.getPercentToDeadline() < 50) {
      return 'rgb(148, 99, 0)';
    } else {
      return '#262338';
    }
  }

  currencySymbol() {
    return getCurrencySymbol(this.proposalTile.insuredAmountCurrency, 'narrow');
  }

  getLineOfBusinessIconName() {
    let imageName = '';
    const number = this.proposalTile.lineOfBusiness.number;

    if (number.startsWith('04')) {
      imageName = 'Haftpflicht';
    } else if (number.startsWith('06')) {
      imageName = 'Luftfahrt';
    } else if (number.startsWith('08')) {
      imageName = 'industrie';
    } else if (number.startsWith('17')) {
      imageName = 'Technische';
    } else if (number.startsWith('18')) {
      imageName = 'Einheitsversicherung';
    } else if (number.startsWith('19')) {
      imageName = 'Transport';
    } else if (number === '231') {
      imageName = 'Feuer';
    } else if (number === '140') {
      imageName = 'Wohngebaeude';
    } else if (number === '232') {
      imageName = 'Technische_Betriebsunterbrechung';
    } else if (number === '233' || number.startsWith('29')) {
      imageName = 'Sonstige_Versicherung';
    } else if (number === '630') {
      imageName = 'Rahmen_und_Sammelvertraege_Multi-Line';
    } else {
      imageName = 'missing';
    }


    return imageName;
  }

  navigateToUserProfile(userId) {
    this.router.navigate([Route.USER_PAGE, userId]);
  }

  navigateToOffers() {
    this.router.navigate(['/' + Route.PROPOSAL_DETAILS, this.proposalTile.id],
      {queryParams: {tab: DetailsPageTab.OFFERS}});
  }

  protected readonly Roles = Roles;
}
